import React from 'react';
import { Shield, AlertTriangle, CheckCircle, Clock, Users, FileText } from 'lucide-react';

export const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Shield className="h-16 w-16 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Terms of Use</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Introduction</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Welcome to PrivateTools. These Terms of Use ("Terms") govern your use of our website and services. 
              By accessing or using PrivateTools, you agree to be bound by these Terms and our Privacy Policy.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              PrivateTools provides a collection of online tools that process data locally in your browser, 
              ensuring your privacy and data security. Our services are designed to be simple, secure, and user-friendly.
            </p>
          </div>

          {/* Acceptance of Terms */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. Acceptance of Terms</h2>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-blue-800 dark:text-blue-400 font-medium mb-2">By using our services, you agree to:</p>
                  <ul className="text-blue-700 dark:text-blue-300 space-y-1 text-sm">
                    <li>• Comply with all applicable laws and regulations</li>
                    <li>• Use our services for lawful purposes only</li>
                    <li>• Respect the intellectual property rights of others</li>
                    <li>• Not attempt to gain unauthorized access to our systems</li>
                    <li>• Not interfere with the proper functioning of our services</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Service Description */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. Service Description</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              PrivateTools offers a comprehensive suite of online tools including:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Image Tools</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Image compression and conversion</li>
                  <li>• Format conversion (JPG, PNG, WEBP)</li>
                  <li>• Image cropping and editing</li>
                  <li>• Base64 encoding/decoding</li>
                </ul>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">PDF Tools</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• PDF merging and splitting</li>
                  <li>• Image to PDF conversion</li>
                  <li>• PDF metadata extraction</li>
                  <li>• Text to PDF conversion</li>
                </ul>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Text Tools</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Text analysis and formatting</li>
                  <li>• Case conversion tools</li>
                  <li>• Bionic reading converter</li>
                  <li>• Text sorting and cleaning</li>
                </ul>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Developer Tools</h3>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Encryption and hashing</li>
                  <li>• Number base conversion</li>
                  <li>• QR code generation</li>
                  <li>• Data format conversion</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Privacy and Data Security */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Privacy and Data Security</h2>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Shield className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-green-800 dark:text-green-400 font-semibold mb-2">Our Privacy Commitment</h3>
                  <ul className="text-green-700 dark:text-green-300 space-y-1 text-sm">
                    <li>• <strong>Local Processing:</strong> All data processing occurs in your browser</li>
                    <li>• <strong>No Data Storage:</strong> We do not store, transmit, or collect your data</li>
                    <li>• <strong>No Tracking:</strong> We do not use cookies or tracking technologies</li>
                    <li>• <strong>No Analytics:</strong> We do not collect usage analytics or personal information</li>
                    <li>• <strong>Secure by Design:</strong> Your data never leaves your device</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* User Responsibilities */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. User Responsibilities</h2>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-yellow-800 dark:text-yellow-400 font-semibold mb-2">Prohibited Activities</h3>
                  <ul className="text-yellow-700 dark:text-yellow-300 space-y-1 text-sm">
                    <li>• Uploading malicious files or content</li>
                    <li>• Attempting to overload our servers</li>
                    <li>• Reverse engineering our tools</li>
                    <li>• Using our services for illegal activities</li>
                    <li>• Violating intellectual property rights</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Intellectual Property</h2>
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-purple-800 dark:text-purple-400 font-semibold mb-2">Ownership Rights</h3>
                  <ul className="text-purple-700 dark:text-purple-300 space-y-1 text-sm">
                    <li>• <strong>Our Rights:</strong> PrivateTools retains all rights to our website, tools, and content</li>
                    <li>• <strong>Your Rights:</strong> You retain all rights to your uploaded content and generated outputs</li>
                    <li>• <strong>Open Source:</strong> Some components may use open-source libraries</li>
                    <li>• <strong>Attribution:</strong> Please respect third-party licenses and attributions</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimers */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Disclaimers</h2>
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h3 className="text-red-800 dark:text-red-400 font-semibold mb-2">Service Availability</h3>
                <p className="text-red-700 dark:text-red-300 text-sm">
                  Our services are provided "as is" without warranties of any kind. We do not guarantee 
                  uninterrupted access or error-free operation. We reserve the right to modify, suspend, 
                  or discontinue services at any time.
                </p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h3 className="text-red-800 dark:text-red-400 font-semibold mb-2">Data Loss</h3>
                <p className="text-red-700 dark:text-red-300 text-sm">
                  While we strive to provide reliable tools, we are not responsible for any data loss, 
                  corruption, or damage that may occur during processing. Users should always backup 
                  important data before using our tools.
                </p>
              </div>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              To the maximum extent permitted by law, PrivateTools shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages, including but not limited to:
            </p>
            <ul className="text-gray-700 dark:text-gray-300 space-y-2 mb-4">
              <li>• Loss of profits, data, or business opportunities</li>
              <li>• Damages resulting from use or inability to use our services</li>
              <li>• Costs of procuring substitute services</li>
              <li>• Any other damages arising from these Terms</li>
            </ul>
          </div>

          {/* Changes to Terms */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Changes to Terms</h2>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-blue-800 dark:text-blue-400 text-sm">
                    We may update these Terms from time to time. We will notify users of any material changes 
                    by updating the "Last updated" date at the top of this page. Your continued use of our 
                    services after such changes constitutes acceptance of the new Terms.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Governing Law */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Governing Law</h2>
            <p className="text-gray-700 dark:text-gray-300">
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction 
              where PrivateTools operates. Any disputes arising from these Terms shall be resolved through 
              appropriate legal channels.
            </p>
          </div>

          {/* Contact Information */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. Contact Information</h2>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Users className="h-6 w-6 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    If you have any questions about these Terms of Use, please review our Privacy Policy 
                    or contact us through our website. We are committed to addressing your concerns 
                    and ensuring a positive user experience.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8 mt-12">
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                By using PrivateTools, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                © 2025 PrivateTools. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 